import apiUseCases from "@src/cloud-connect/api";
import { parseAxiosApiError } from "@src/cloud-connect/utils/parse-axios-api-error";
import { Widget, WidgetPositioning } from "@src/types/dashboards";
import store from "@src/reducers/store";

export interface PostDuplicateWidgetV2Result {
  success: boolean;
  /** Server-assigned widget id + finalised placement on success — the caller
   *  dispatches it into the dashboard so the new tile renders immediately
   *  and the grid picks up the BE-issued position. */
  widget?: Widget;
  error?: string;
}

/** Clone an existing widget, forwarding the live canvas positioning as the
 *  BE-expected `widget_positioning` JSON field. On success returns the
 *  committed widget (server-assigned id + finalised placement). The caller
 *  splices it into the dashboard reducer so the new tile renders without
 *  going through the drawer. `positioning` MUST come from
 *  `selectedDashboard.positioning[<id>]` (GridStack is the source of truth)
 *  and the BE may nudge it (e.g. per doc §2.5.1 the copy goes below
 *  the original). */
export const postDuplicateWidgetV2UseCase = async (
  dashboardId: string,
  widgetId: string,
  positioning: WidgetPositioning,
): Promise<PostDuplicateWidgetV2Result> => {
  try {
    const authContext = store.getState().yourSlice.authContext;
    if (!authContext) {
      return { success: false, error: "Not authenticated" };
    }
    const response = await apiUseCases.postDuplicateWidgetV2(
      authContext.customerNamespace,
      dashboardId,
      widgetId,
      positioning,
    );
    return { success: true, widget: response.data };
  } catch (error) {
    return {
      success: false,
      error: parseAxiosApiError(error, "Failed to duplicate widget").message,
    };
  }
};
